/**
 * StudyTrustSurface — a stateless Study-Cursor subscriber around TrustSurfacePanel
 * (FXC-9009 two-rooms redesign).
 *
 * Adversarial-review fix (CRITICAL): the 3D lanes previously derived their own
 * UNDEBOUNCED `fixed` record and let TrustSurfacePanel fetch under its own
 * queryKey/grid — a pin-slider drag refetched per tick against the
 * single-worker API, cache-missing the 2D board's identical surface. Now the
 * lane consumes the SAME debounced slice spec + canonical study-surface query
 * as every other lane (useDebouncedStudySlice/useStudySurface) and hands the
 * fetched data to the props-only TrustSurfacePanel via its `surface`
 * injection seam — one fetch per (quantity, slice), shared by all lanes.
 *
 * It also wires the SHARED 3D camera (§5.4 view3d): every 3D lane reads
 * cursor.view3d and writes it back on camera moves, so lanes stay in lockstep
 * and the camera survives demotion/promotion and reloads. Evidence mode maps
 * onto the panel's fused/per-model toggle (corrected → fused blend,
 * raw/compatible → the bare per-model layers) so 3D lanes answer the one
 * shared control like every 2D card.
 */
import type { AxisSpec } from "../../adaptive-sampling/types";
import type { StudyView3d } from "../../adaptive-sampling/surfaces/useSliceState";
import { TrustSurfacePanel } from "../../thread-v5/TrustSurfacePanel";
import { useDebouncedStudySlice, useStudySurface } from "./useStudySurface";

export function StudyTrustSurface({
  projectId,
  branch,
  inputs,
  quantity,
  fill,
  pollMs = false,
}: {
  projectId: string;
  branch: string;
  /** Axis metadata for the cursor's ensure() guard + held-value derivation. */
  inputs: readonly AxisSpec[];
  /** Optional local override of the cursor's headline output (panels may override). */
  quantity?: string;
  fill?: boolean;
  /** Poll cadence while a campaign animates the board (the shared query dedupes). */
  pollMs?: number | false;
}) {
  const { cursor, patchCursor, spec } = useDebouncedStudySlice(projectId, branch, inputs);
  const output = quantity ?? cursor.selectedOutput;
  const surface = useStudySurface(projectId, branch, spec, output, { pollMs });
  if (!spec.xInput || !spec.yInput || spec.xInput === spec.yInput) return null;
  return (
    <TrustSurfacePanel
      projectId={projectId}
      branch={branch}
      quantity={output}
      axisX={spec.xInput}
      axisY={spec.yInput}
      fixed={spec.fixed}
      fill={fill}
      surface={surface.data}
      surfaceLoading={surface.isLoading}
      surfaceError={surface.isError ? (surface.error as Error) : null}
      view={cursor.view3d}
      onViewChange={(view: StudyView3d) => patchCursor({ view3d: view })}
      fused={cursor.evidenceMode === "corrected"}
      onFusedChange={(fused) => patchCursor({ evidenceMode: fused ? "corrected" : "raw" })}
    />
  );
}
